import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { RootState } from 'store/ducks';
//components
import { TooltipIcon } from 'pages/Quotes/components/TooltipIcon';
import { useCustomEvent } from 'components/ApplicationInsights';
import { Visibility } from 'components/Visibility';
import { Row, Column } from '../Layout/Grid';
import { Button } from '../Button';
import Text from '../Text';
//interface
import { CategoryType, ITabNavigation } from './interface';
import { StatusType } from 'components/Table/interface';
//context
import { useTabsContext } from 'contexts/Tabs';
import { useDetailsQuoteContext } from 'contexts/DetailsQuote';
//assets
import IconNegotiation from "img/tab-negotiation-icon.svg";
//utils
import { checkDistributionCenterSelected } from 'pages/Quotes/Details/utils';
//styles
import './styles.css';

const TabNavigation: React.FC<ITabNavigation> = ({
  distributionCenterSelected,
  allBudgetsSavedInQuote,
  firstDetailPageRender,
  portalNavigation,
  productSelected,
  setCurrentPage,
  isQuoteExpired,
  trackingEvent,
  setTabFilter,
  trackingKey,
  modalIsOpen,
  singlePage,
  className,
  tabLabels,
  pages,
  icon
}) => {
  const { disableTabs } = useTabsContext();
  const currentTabPageStore = useSelector((state: RootState) => state.currentTab);

  const location = useLocation();
  const dispatch = useDispatch();
  const { quoteDetail, setModalPaymentTermModal } = useDetailsQuoteContext();
  const trackChangeTab = useCustomEvent(trackingKey, trackingEvent ?? 'Trocar Aba');

  const [currentTab, setCurrentTab] = useState(currentTabPageStore?.currentTabPage ?? 0);

  function changeCurrentTab(page: number, filter: string) {
    portalNavigation && dispatch({ type: 'CLOSE_TOAST' });
    setModalPaymentTermModal(false);
    setCurrentPage && setCurrentPage(0);
    setCurrentTab(page);
    setTabFilter && setTabFilter(filter);
    trackChangeTab({ 'item': tabLabels[page].trackingName });
  }

  const checkAllBudgetsSavedAndChangeTab = (page: number, filter: string, isDiscarded?: boolean) => {
    changeCurrentTab(page, filter);
    allBudgetsSavedInQuote!(tabLabels[page] && tabLabels[page].trackingName, isDiscarded);
  };

  const handleSendUpdateBudget = (page: number, filter: string) => {
    const tab = tabLabels[page];

    const isTabValid = !!tab;
    const hasSelectedProducts = !!productSelected && productSelected.length > 0;
    const isDefaultDistributionCenter = distributionCenterSelected === "default";
    const isTabOutPortfolio = tab?.trackingName === "outPortfolio";
    const isQuoteProcessing = quoteDetail?.status === StatusType.PROCESSING;
    const canChangeDirectly = hasSelectedProducts && isDefaultDistributionCenter && isQuoteExpired;
  
    const requiresDistributionCenterCheck = hasSelectedProducts &&
      (tab?.trackingName === "default" || (isDefaultDistributionCenter && !isTabOutPortfolio)) &&
      !isQuoteProcessing &&
      distributionCenterSelected !== "outPortfolio";
  
    if (!isTabValid) return;

    if (canChangeDirectly) {
      changeCurrentTab(page, filter);
      allBudgetsSavedInQuote?.();
    } else if (requiresDistributionCenterCheck) {
      checkDistributionCenterSelected(
        distributionCenterSelected!,
        tab.trackingName!,
        dispatch,
        setModalPaymentTermModal,
        checkAllBudgetsSavedAndChangeTab,
        page,
        filter
      );
    } else {
      changeCurrentTab(page, filter);
      allBudgetsSavedInQuote?.(tab.trackingName!);
    }
  };

  useEffect(() => {
    setCurrentTab(0);

    const typeTab: boolean | any = location?.state;

    if (typeTab?.politicBusiness)
      setCurrentTab(1);

    if (typeTab?.regulatoryDocument)
      setCurrentTab(2);
  }, [location]);

  useEffect(() => {
    if (currentTabPageStore?.currentNameTab === 'outPortfolio')
      setCurrentTab(currentTabPageStore.currentTabPage ?? 0);
  }, [currentTabPageStore]);

  useEffect(() => {
    if ((modalIsOpen?.isOpen === false && modalIsOpen?.isCancelButtonClicked === true)
      || modalIsOpen?.firstLoad === true || firstDetailPageRender === true) {
      setCurrentTab(0);
    }
  }, [modalIsOpen, firstDetailPageRender]);

  const handleColor = (index: number, tabCurrent: number) => {
    if (tabCurrent === index && tabLabels[index].filter === CategoryType.MEDICINE) {
      return "medicineCategory";
    } else if (tabCurrent === index && tabLabels[index].filter === CategoryType.MATERIAL) {
      return "materialCategory";
    } else if (tabCurrent === index && tabLabels[index].filter === CategoryType.NUTRITION) {
      return "nutritionCategory";
    } else if (tabCurrent === index) {
      return "defaultTabColor";
    } else {
      return "color-dark-gray";
    }
  };

  const handleTabClick = (index: number, filter: any) => {
    if (portalNavigation && filter?.distributionCenter !== "outPortfolio") {
      handleSendUpdateBudget(index, filter);
    } else {
      changeCurrentTab(index, filter);
    }
  };

  return (
    <Column className={`${className}`}>
      <Row className="justify-between row-break">
        <Row className="z-2 d-sm-flex overflow-y-auto">
          {tabLabels?.map((tab, _) => (
            <Button
              disabled={disableTabs ?? false}
              className={`${_ > 0 && 'ml-3'}`}
              width="auto"
              key={_}
              onClick={() => handleTabClick(_, tab.filter)}
            >
              <Text className={`font-bold pb-1 ${handleColor(_, currentTab)}`}>
                {tab.label}
              </Text>

              <Visibility visible={tab.previouslyAddedToBionexo && !!icon && !tab?.negotiation}>
                <Row className="iconWarning">
                  {icon}
                </Row>
              </Visibility>

              <Visibility visible={tab?.negotiation}>
                <Row className="iconWarning">
                  <TooltipIcon alt="openToNegotiationIcon" icon={IconNegotiation} tooltip="Aberto para negociação" />
                </Row>
              </Visibility>
            </Button>
          ))}
        </Row>
      </Row>

      <Visibility visible={!singlePage}>
        {pages.map(
          (page, _) =>
            _ === currentTab && (
              <Column key={_} className="animated fadeInUp">
                {page}
              </Column>
            )
        )}
      </Visibility>

      <Visibility visible={singlePage || false}>
        <Column key={1} className="fadeInUp">
          {pages[0]}
        </Column>
      </Visibility>
    </Column>
  );
};

export default TabNavigation;
